import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { updateLegalCase } from '../../services/api/implementation/impl';
import { toDateOnly } from './taskWorkflow';
import DueDateReasonModal from './DueDateReasonModal';
import LegalCaseFields from './LegalCaseFields';
import {
    CaseStatus,
    REOPEN_REASON_MESSAGE,
    TARGET_REQUIRED_MESSAGE,
    legalCaseError,
    legalCaseFormValues,
    legalCasePayload,
    targetDateChangeNeedsReason
} from './legalCase';
import { useLegalCaseOptions, useProjectUserOptions } from './legalCaseOptions';

// A court case's fields, case status and target date change only through
// PUT /task/:id/legal — the generic task PUT refuses them.
const EditLegalCaseModal = ({ task, onClose }) => {
    const queryClient = useQueryClient();
    const options = useLegalCaseOptions();
    const people = useProjectUserOptions(task.project_id);
    const legalCase = task.legalCase || {};
    const wasClosed = legalCase.caseStatus === CaseStatus.CLOSED;
    // A slipped target date waits here while the user gives the reason.
    const [pendingPayload, setPendingPayload] = useState(null);

    const { register, handleSubmit, watch } = useForm({
        defaultValues: {
            legalCase: legalCaseFormValues(legalCase),
            targetDate: toDateOnly(task.dueDate),
            reopenReason: ''
        }
    });
    const reopening = wasClosed && watch('legalCase.caseStatus') !== CaseStatus.CLOSED;

    const updateMutation = useMutation({
        mutationFn: (payload) => updateLegalCase(task.id, payload),
        onSuccess: () => {
            toast.success('Court case updated');
            queryClient.invalidateQueries(['managementTask']);
            queryClient.invalidateQueries(['managementTasks']);
            onClose();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to update the court case');
        }
    });

    const onSubmit = (data) => {
        const payload = { ...legalCasePayload(data.legalCase), targetDate: data.targetDate || null };
        const error =
            legalCaseError(payload) ||
            (!payload.targetDate ? TARGET_REQUIRED_MESSAGE : null) ||
            (reopening && !String(data.reopenReason || '').trim() ? REOPEN_REASON_MESSAGE : null);
        if (error) {
            toast.error(error);
            return;
        }
        if (reopening) payload.reopenReason = data.reopenReason.trim();
        if (
            targetDateChangeNeedsReason({
                fromDate: task.dueDate,
                toDate: payload.targetDate,
                fromAction: legalCase.nextAction,
                toAction: payload.nextAction
            })
        ) {
            setPendingPayload(payload);
            return;
        }
        updateMutation.mutate(payload);
    };

    return (
        <>
            <Modal
                name={`Edit Court Case ${legalCase.caseCode || ''}`.trim()}
                width={70}
                onClose={onClose}
                hideClose={false}
            >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {options.loaded ? (
                        <div className="grid grid-cols-2 gap-4">
                            <FormInput
                                label="Target date"
                                register={register}
                                register_key="targetDate"
                                type="date"
                                isRequired={true}
                                width={100}
                            />
                            <p className="self-end text-xs text-gray-500">
                                When the next action is due. Moving it later for the same next
                                action needs a reason.
                            </p>
                            <LegalCaseFields
                                register={register}
                                watch={watch}
                                options={options}
                                userOptions={people}
                            />
                            {reopening && (
                                <div className="col-span-2">
                                    <FormInput
                                        label="Reason for reopening"
                                        register={register}
                                        register_key="reopenReason"
                                        type="textarea"
                                        isRequired={true}
                                        width={100}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">Loading court case fields…</p>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!options.loaded || updateMutation.isPending}
                            className="bg-[#346C75] text-white px-8 py-2 rounded hover:bg-[#2a565e] disabled:opacity-50"
                        >
                            {updateMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Modal>
            {pendingPayload && (
                <DueDateReasonModal
                    title="Reason for moving the target date"
                    body="The target date is moving later while the next action stays the same. Say why."
                    onClose={() => setPendingPayload(null)}
                    onConfirm={(reason) => {
                        const next = { ...pendingPayload, dueDateChangeReason: reason };
                        setPendingPayload(null);
                        updateMutation.mutate(next);
                    }}
                />
            )}
        </>
    );
};

export default EditLegalCaseModal;
